import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, tokenStore, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [status, setStatus] = useState("loading"); // loading | authed | anon

    const fetchMe = useCallback(async () => {
        try {
            const { data } = await api.get("/me");
            setUser(data);
            setStatus("authed");
        } catch {
            setUser(null);
            setStatus("anon");
        }
    }, []);

    useEffect(() => {
        if (!tokenStore.getAccess()) {
            setStatus("anon");
            return;
        }
        fetchMe();
    }, [fetchMe]);

    const login = useCallback(async (email, password) => {
        try {
            const { data } = await api.post("/auth/login", { email, password });
            tokenStore.set(data.access_token, data.refresh_token);
            setUser(data.user);
            setStatus("authed");
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiError(e) };
        }
    }, []);

    const logout = useCallback(async () => {
        try {
            await api.post("/auth/logout", { refresh_token: tokenStore.getRefresh() });
        } catch {
            /* ignore */
        }
        tokenStore.clear();
        setUser(null);
        setStatus("anon");
    }, []);

    // Freeze the context value so consumers only re-render when something
    // actually changes — avoids cascading re-renders across every screen
    // whenever an unrelated parent re-renders.
    const value = useMemo(
        () => ({ user, status, login, logout, refreshUser: fetchMe, setUser }),
        [user, status, login, logout, fetchMe],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
    return ctx;
}
